import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, Check, X, MessageCircle, MonitorPlay } from "lucide-react";
import { IMAGES, ONLINE_WHATSAPP } from "@/lib/content";
import { KineticLines } from "@/components/landing/Reveal";
import { EligibilityButton } from "@/components/landing/EligibilityButton";

const alone = [
  "Repeating the same losses",
  "Random tips from random groups",
  "Trading on fear and greed",
  "No one reviews your trades",
];

const withUs = [
  "A written plan with fixed risk per trade",
  "Live mentor on screen every session",
  "10 planned live trades, each reviewed",
  "Recordings + community support",
];

const heroBullets = [
  "A written trading plan with fixed risk on every trade",
  "Live mentor on screen in every session, plus recordings",
  "90-day roadmap built for losing traders and beginners",
];

export const OnlineHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const glowY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section ref={ref} className="relative overflow-hidden diag-grid" data-testid="online-hero-section">
      <motion.div
        style={{ y: glowY }}
        className="pointer-events-none absolute left-1/4 top-10 h-[34rem] w-[34rem] -translate-x-1/2 rounded-full bg-em1/25 blur-[130px]"
      />
      <div className="pointer-events-none absolute -right-40 top-40 h-96 w-96 rounded-full bg-em2/10 blur-[120px]" />

      <div className="relative z-10 mx-auto grid max-w-7xl gap-12 px-4 pb-16 pt-12 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pb-24 lg:pt-20">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span
              className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-surface px-4 py-1.5 text-xs font-semibold text-gold"
              data-testid="online-hero-badge"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-em2 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-em2" />
              </span>
              For Losing Traders & Serious Beginners · Live Online · 90 Days
            </span>
          </motion.div>

          <KineticLines
            className="mt-6 font-display text-[2.5rem] font-bold uppercase leading-[0.95] tracking-tight text-offwhite sm:text-6xl lg:text-[3.9rem]"
            lines={[
              "Stop Losing Money",
              "to Guesswork.",
              "Place 10 Planned Live Trades",
              "With Fixed Risk in 90 Days,",
              "With a Mentor Reviewing Every One.",
            ]}
            accentIndex={4}
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-mutedgreen sm:text-lg"
          >
            For traders who keep losing money and beginners serious about learning
            the skill. No tips. No guesswork. No learning alone. You build your own
            written trading plan and apply it in live markets, with a mentor beside
            you on screen. Try your first 2 sessions risk-free.
          </motion.p>

          <motion.ul
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.1 }}
            className="mt-6 space-y-2.5"
          >
            {heroBullets.map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm text-offwhite sm:text-base">
                <Check size={18} className="mt-0.5 shrink-0 text-em2" />
                {b}
              </li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            className="mt-8 flex flex-col items-start gap-4"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <EligibilityButton size="lg" label="Reserve My Free Trial Seat" testId="online-hero-cta" />
              <a
                href={ONLINE_WHATSAPP}
                target="_blank"
                rel="noreferrer"
                data-testid="online-hero-whatsapp-btn"
                className="inline-flex min-h-[58px] items-center justify-center gap-2 rounded-full border border-em2/40 bg-em2/10 px-7 text-base font-semibold text-em2 transition-colors hover:bg-em2/20"
              >
                <MessageCircle size={18} /> WhatsApp Us
              </a>
            </div>
            <div className="flex items-center gap-2 text-sm text-offwhite">
              <span className="flex text-gold">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={15} fill="currentColor" />
                ))}
              </span>
              1,000+ Students Trained · 200+ Five-Star Reviews · 10+ Years of Experience
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-3xl border border-surfaceline glow-emerald">
              <motion.img
                style={{ y: imgY }}
                src={IMAGES.tradingDesk}
                alt="Live online trading session with mentor on screen"
                className="h-80 w-full object-cover sm:h-96 lg:h-[28rem]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/25 to-transparent" />
              <div className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full border border-em2/40 bg-ink/70 px-3 py-1.5 text-xs font-semibold text-em2 backdrop-blur">
                <MonitorPlay size={14} /> Live Online Session · Your Mentor Reviews Your Trade Plan in Real Time
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-red-500/25 bg-red-500/[0.06] p-4">
                <p className="mb-2 flex items-center gap-1.5 text-[0.7rem] font-bold uppercase tracking-wider text-red-400">
                  <X size={13} /> Alone
                </p>
                <ul className="space-y-1.5">
                  {alone.map((c) => (
                    <li key={c} className="flex items-start gap-1.5 text-xs text-mutedgreen">
                      <X size={13} className="mt-0.5 shrink-0 text-red-400/80" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-em2/30 bg-em2/[0.07] p-4">
                <p className="mb-2 flex items-center gap-1.5 text-[0.7rem] font-bold uppercase tracking-wider text-em2">
                  <Check size={13} /> BullsEdge Online (90 Days)
                </p>
                <ul className="space-y-1.5">
                  {withUs.map((c) => (
                    <li key={c} className="flex items-start gap-1.5 text-xs text-offwhite">
                      <Check size={13} className="mt-0.5 shrink-0 text-em2" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="gold-divider" />
      </div>
    </section>
  );
};
